import React, { useEffect, useRef, useState } from 'react';
import { Delete, CornerDownLeft, Hash } from 'lucide-react';

interface Props {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  /** Qué se está eligiendo, para el título de la emergente y los lectores de pantalla. */
  label: string;
  /** Unidad tras el número en el botón ("preguntas", "min"). */
  unit?: string;
  className?: string;
}

/**
 * Un número que se elige con un teclado numérico emergente.
 *
 * El botón muestra el valor; al pulsarlo sale el teclado en una ventana
 * encima, y "Introducir" (o Enter) lo cierra y deja seguir rellenando el resto
 * del formulario. Antes el teclado estaba siempre desplegado debajo del campo
 * y ocupaba media pantalla en el teléfono para un dato que se toca una vez.
 *
 * Mientras se teclea se guarda el texto tal cual: si se ajustara en cada
 * tecla, al escribir "12" el "1" saltaría al mínimo y no habría forma de
 * llegar. El ajuste al rango se hace al introducir.
 */
export const NumberPadField: React.FC<Props> = ({ value, onChange, min, max, label, unit, className }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={`${label}: ${value}${unit ? ` ${unit}` : ''}. Pulsa para cambiarlo.`}
        className={`inline-flex items-center gap-2 h-11 pl-3 pr-4 rounded-lg bg-ink-850 border border-ink-700
                    text-ink-100 hover:border-sky-600 transition-colors ${className ?? ''}`}
      >
        <Hash className="w-4 h-4 text-sky-400" />
        <span className="text-lg font-semibold tabular-nums">{value}</span>
        {unit && <span className="text-sm text-ink-400">{unit}</span>}
      </button>
      {open && (
        <NumberPadDialog
          initial={value}
          min={min}
          max={max}
          label={label}
          unit={unit}
          onEnter={(v) => {
            onChange(v);
            setOpen(false);
          }}
          onCancel={() => setOpen(false)}
        />
      )}
    </>
  );
};

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', 'del'] as const;

const NumberPadDialog: React.FC<{
  initial: number;
  min: number;
  max: number;
  label: string;
  unit?: string;
  onEnter: (value: number) => void;
  onCancel: () => void;
}> = ({ initial, min, max, label, unit, onEnter, onCancel }) => {
  const [text, setText] = useState(String(initial));
  // La primera cifra sustituye al valor que había, como en un campo con el
  // texto seleccionado: abrir el teclado es para poner otro número.
  const fresh = useRef(true);
  const enterRef = useRef<HTMLButtonElement>(null);
  const digits = String(max).length;
  const parsed = Number(text);
  const clamped = Math.min(max, Math.max(min, Number.isFinite(parsed) && text !== '' ? parsed : initial));
  const adjusted = text !== '' && clamped !== parsed;

  const press = (k: (typeof KEYS)[number]) => {
    const first = fresh.current;
    fresh.current = false;
    setText((prev) => {
      if (k === 'del') return first ? '' : prev.slice(0, -1);
      if (k === 'C') return '';
      if (first) return k;
      // Con todas las cifras ya escritas, la siguiente empieza de nuevo.
      if (prev.length >= digits) return k;
      return prev === '0' ? k : prev + k;
    });
  };

  const enter = () => onEnter(clamped);

  // El teclado físico también vale: cifras, borrar, Enter y Escape.
  useEffect(() => {
    enterRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        press(e.key as (typeof KEYS)[number]);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        press('del');
      } else if (e.key === 'Enter') {
        e.preventDefault();
        enterRef.current?.click();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="fixed inset-0 z-[60] bg-ink-950/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onCancel()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="numpad-title"
    >
      <div className="surface-raised bg-ink-900 w-full max-w-xs p-5 space-y-4 animate-pop">
        <div className="text-center space-y-1">
          <p id="numpad-title" className="text-sm font-medium text-ink-300">
            {label}
          </p>
          <p
            className="font-mono text-5xl font-black tabular-nums text-ink-50 min-h-[3.5rem]"
            aria-live="polite"
          >
            {text || <span className="text-ink-600">–</span>}
            {unit && <span className="text-base font-sans font-medium text-ink-500 ml-2">{unit}</span>}
          </p>
          <p className="text-xs text-ink-500">
            entre {min} y {max}
            {adjusted && <> · se usarán {clamped}</>}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2" aria-label="Teclado numérico">
          {KEYS.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => press(k)}
              aria-label={k === 'del' ? 'Borrar' : k === 'C' ? 'Vaciar' : k}
              className="h-14 rounded-xl bg-ink-850 border border-ink-700 text-ink-100 text-xl font-semibold
                         hover:border-sky-600 hover:text-sky-300 active:bg-ink-800 transition-colors
                         flex items-center justify-center"
            >
              {k === 'del' ? <Delete className="w-5 h-5" /> : k}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 h-12 rounded-xl font-semibold bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-200 transition-colors"
          >
            Cancelar
          </button>
          <button
            ref={enterRef}
            type="button"
            onClick={enter}
            className="flex-[2] h-12 rounded-xl font-bold bg-sky-600 hover:bg-sky-500 text-white transition-colors
                       flex items-center justify-center gap-2"
          >
            <CornerDownLeft className="w-5 h-5" />
            Introducir
          </button>
        </div>
      </div>
    </div>
  );
};
